import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { format as formatDate } from "date-fns";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Lock, Unlock, RefreshCw, Car, AlertCircle, CheckCircle2, XCircle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

/**
 * Admin-only remote Lock/Unlock for the org's DroneMobile vehicles, plus a
 * history of every command (who, which unit, which car, when, success/fail).
 * Rendered inside Settings > Patrol > Vehicles (already admin-gated by route).
 */
export default function PatrolVehicles({ source = "settings" }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // device_key currently sending a command -> "lock" | "unlock"
  const [pending, setPending] = useState({});
  const [logs, setLogs] = useState([]);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/drone/vehicles`, { withCredentials: true });
      setVehicles(res.data?.vehicles || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load DroneMobile vehicles");
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/drone/logs`, {
        params: { limit: 100 },
        withCredentials: true,
      });
      setLogs(res.data?.logs || []);
    } catch {
      // History is non-critical; leave whatever we had.
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
    fetchLogs();
  }, [fetchVehicles, fetchLogs]);

  const sendCommand = async (vehicle, command) => {
    setPending((p) => ({ ...p, [vehicle.device_key]: command }));
    try {
      await axios.post(
        `${API}/drone/${command}`,
        { device_key: vehicle.device_key, vehicle_name: vehicle.name, source },
        { withCredentials: true }
      );
      toast.success(`${command === "lock" ? "Lock" : "Unlock"} sent to ${vehicle.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${command} ${vehicle.name}`);
    } finally {
      setPending((p) => {
        const next = { ...p };
        delete next[vehicle.device_key];
        return next;
      });
      fetchLogs();
    }
  };

  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      return formatDate(new Date(iso), "M/d/yy HH:mm");
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6 pb-28 sm:pb-4">
      <div>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
              <Car className="w-5 h-5" /> Vehicle Remote Control
            </h3>
            <p className="text-sm text-slate-400">
              Lock or unlock DroneMobile-equipped vehicles.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={fetchVehicles} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {error && (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-red-900 bg-red-950/40 p-3 text-sm text-red-300">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {loading && vehicles.length === 0 && (
          <p className="mt-3 text-sm text-slate-400">Loading vehicles…</p>
        )}

        {!loading && !error && vehicles.length === 0 && (
          <p className="mt-3 text-sm text-slate-400">
            No vehicles found on the DroneMobile account.
          </p>
        )}

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {vehicles.map((v) => {
            const busy = pending[v.device_key];
            return (
              <Card key={v.device_key} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4 space-y-3">
                  <div className="min-w-0">
                    <div className="font-medium text-slate-100 truncate">{v.name}</div>
                    {(v.make || v.model) && (
                      <div className="text-xs text-slate-400 truncate">
                        {[v.make, v.model].filter(Boolean).join(" ")}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          className="flex-1 bg-emerald-700 hover:bg-emerald-600"
                          disabled={!!busy}
                        >
                          <Lock className="w-4 h-4 mr-1" />
                          {busy === "lock" ? "Locking…" : "Lock"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-900 border-slate-800">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Lock {v.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This sends a remote lock command to {v.name}.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-emerald-700 hover:bg-emerald-600"
                            onClick={() => sendCommand(v, "lock")}
                          >
                            <Lock className="w-4 h-4 mr-1" />Lock
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1"
                          disabled={!!busy}
                        >
                          <Unlock className="w-4 h-4 mr-1" />
                          {busy === "unlock" ? "Unlocking…" : "Unlock"}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-slate-900 border-slate-800">
                        <AlertDialogHeader>
                          <AlertDialogTitle>Unlock {v.name}?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This sends a remote unlock command to {v.name}.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            className="bg-amber-600 hover:bg-amber-700"
                            onClick={() => sendCommand(v, "unlock")}
                          >
                            <Unlock className="w-4 h-4 mr-1" />Unlock
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Command history */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-100">Command History</h3>
          <Button variant="outline" size="sm" onClick={fetchLogs}>
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>

        {logs.length === 0 ? (
          <p className="text-sm text-slate-400">No commands sent yet.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => (
              <div
                key={l.log_id}
                className="rounded-md border border-slate-700 bg-slate-800/60 px-3 py-2 text-sm"
              >
                <div className="flex items-center gap-2">
                  {l.success ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                  )}
                  <span
                    className={`font-medium uppercase shrink-0 ${
                      l.command === "lock" ? "text-emerald-400" : "text-amber-400"
                    }`}
                  >
                    {l.command}
                  </span>
                  <span className="text-slate-200 truncate">{l.vehicle_name}</span>
                  <span className="ml-auto text-xs text-slate-500 whitespace-nowrap shrink-0">
                    {fmtTime(l.created_at)}
                  </span>
                </div>
                <div className="mt-0.5 pl-6 text-xs text-slate-400 break-words">
                  {l.user_name}
                  {l.unit ? ` · Unit ${l.unit}` : ""}
                  {l.source ? ` · ${l.source}` : ""}
                  {!l.success && l.detail ? (
                    <span className="text-red-400"> — {l.detail}</span>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
